import { tagFor, keyOf } from './conversions';

/*
  WORKING A RUN OUT. Ordering stage 4, 12 October 2026.

  **Pure**: no database, so every rule can be tested. It takes a job's lines and
  everything the morning reports and the rule pages say about them, and gives
  back, for each line, a route (order, Austin, skip or hold), the item and
  quantity to use, why, and the arithmetic behind it. Then each vendor's total
  against its minimum. **He has the last word**: anything he changed by hand
  comes in as an override and is kept, and the plan is worked out again around
  it, so raising a line can carry a vendor over its minimum.

  His steps, in his order (docs/orders/DOOR-4-THE-CLAIMSOFT-RUN.md):

  1. Conversions. A line in a family gets the family's par minus on hand plus on
     order, at this warehouse only, across every number in the family; the par
     sits on one of them. The end of the chain is proposed as the item. Two
     lines in one family are counted once. "Do not use, no replacement" is held
     for him (his answer of 12 October). Pending, needed and to reorder on the
     Stock Status are ignored (his answer the same day).
  2. Item rules (exact number before prefix), then the vendor's special mode.
  3. A live back order on the item from that vendor is held, with its
     substitute named.
  4. Each vendor's total for this warehouse against its minimum: at or above
     it, every line is ordered ("I would prefer all product to be order through
     the vendor and not split up the vendor products"); below it, a line Austin
     can cover whole moves from Austin, and the rest is skipped. An item is
     never split. An item skipped on two earlier runs for this office forces its
     vendor's whole order on the third.
*/

export const ROUTES = {
  order:    'Order from the vendor',
  transfer: 'Move from Austin',
  skip:     'Skip',
  hold:     'Hold for you',
};

const money = n => '$' + Number(n || 0).toFixed(2);

/* The item rule for an item number: an exact rule beats a prefix, and the
   longest prefix wins. */
export function itemRuleFor(rules, number) {
  const k = keyOf(number);
  const exact = rules.find(r => !r.is_prefix && keyOf(r.item_or_prefix) === k);
  if (exact) return exact;
  return rules.filter(r => r.is_prefix && k.startsWith(keyOf(r.item_or_prefix)))
    .sort((a, b) => b.item_or_prefix.length - a.item_or_prefix.length)[0] || null;
}

/* A quantity raised to an item's minimum and rounded up to its multiple. */
export function applyQuantityRule(qty, rule) {
  let q = qty, why = [];
  if (!rule || !(q > 0)) return { qty: q, why };
  if (rule.min_quantity && q < rule.min_quantity) { q = rule.min_quantity; why.push('raised to the minimum of ' + rule.min_quantity); }
  if (rule.order_multiple && q % rule.order_multiple) {
    q = Math.ceil(q / rule.order_multiple) * rule.order_multiple;
    why.push('rounded up to a multiple of ' + rule.order_multiple);
  }
  return { qty: q, why };
}

/*
  input:
    lines       the job's lines: { id, line_no, item_as_given, quantity }
    families    buildFamilies() of the conversions, or null
    stock       Map item key -> { par, onHand, onOrder } at this warehouse, or null when no Stock Status
    items       Map item key -> { catalog_item_id, vendor_id, vendor, ae_number, cost } for every number involved
    austin      Map Account Edge key -> { spare }, or null when no Excess Calc
    vendorRules Map vendor id -> { min_order_value, special_mode }
    itemRules   [{ item_or_prefix, is_prefix, routing_override, min_quantity, order_multiple }]
    backOrders  [{ vendor_id, item_number, use_instead, expected_until }] still live
    priorSkips  Map item key -> how many earlier runs for this office skipped it
    overrides   Map line id -> { item?, qty?, route? } he set by hand
*/
export function planRun(input) {
  const { lines, families, stock, items, austin, vendorRules, itemRules = [], backOrders = [], priorSkips = new Map(), overrides = new Map() } = input;
  const out = new Map();
  const famSeen = new Map();     // family root -> first line_no

  // 1. Conversions, and the quantity and item each line starts from.
  for (const l of [...lines].sort((a, b) => a.line_no - b.line_no)) {
    const given = String(l.item_as_given).trim();
    const tag = families ? tagFor(families, given) : null;
    const p = { id: l.id, line_no: l.line_no, given, askedQty: Number(l.quantity), item: given, qty: Number(l.quantity),
      route: null, reason: [], warnings: [], audit: null };

    if (tag && tag.kind === 'no_replacement') {
      p.route = 'hold';
      p.reason.push('Marked do not use, no replacement: yours to decide');
    } else if (tag) {
      const root = families.familyOf.get(keyOf(given));
      const members = families.members.get(root);
      if (tag.kind === 'replaced') {
        p.item = tag.finals[0];
        p.reason.push('Replaced: ' + given + ' becomes ' + tag.finals[0]);
        if (tag.finals.length > 1) p.warnings.push('Replaced by more than one item: ' + tag.finals.join(' or ') + '. Pick one.');
      }
      if (famSeen.has(root)) {
        p.qty = 0;
        p.route = 'skip';
        p.reason.push('Same product as line ' + famSeen.get(root) + ', counted once there');
      } else {
        famSeen.set(root, l.line_no);
        if (stock) {
          let par = 0, onHand = 0, onOrder = 0;
          const each = [];
          for (const m of members) {
            const s = stock.get(m);
            if (s) { par += s.par; onHand += s.onHand; onOrder += s.onOrder; each.push({ item: families.spell.get(m), ...s }); }
          }
          const needed = Math.max(0, par - onHand - onOrder);
          p.audit = { family: tag.family, par, onHand, onOrder, needed, each };
          p.qty = needed;
          p.reason.push('True need = Par ' + par + ' - On hand ' + onHand + ' - On order ' + onOrder + ' = ' + needed);
          if (!each.length) p.warnings.push('No number in this family is on the Stock Status for this office, so no par was found.');
        } else {
          p.warnings.push('No Stock Status to audit this family with, so the quantity is as ClaimSoft asked.');
        }
      }
    }

    // His hand: the item and the quantity he chose win over the arithmetic.
    const o = overrides.get(l.id);
    if (o && o.item != null && String(o.item).trim() && keyOf(o.item) !== keyOf(p.item)) { p.item = String(o.item).trim(); p.reason.push('Item set by you'); }
    if (o && o.qty != null && o.qty !== '' && Number(o.qty) !== p.qty) { p.qty = Math.max(0, Number(o.qty)); p.reason.push('Quantity set by you'); }

    const info = items.get(keyOf(p.item)) || null;
    p.catalog_item_id = info ? info.catalog_item_id : null;
    p.vendor_id = info ? info.vendor_id : null;
    p.vendor = info ? info.vendor : null;
    p.ae_number = info ? info.ae_number : null;
    p.cost = info && info.cost != null ? Number(info.cost) : null;
    if (!info) p.warnings.push(p.item + ' is not in our items, so it has no vendor, cost or Account Edge number.');
    else if (p.cost == null) p.warnings.push('No cost for ' + p.item + ', so it counts as $0 toward the vendor minimum.');
    out.set(l.id, { p, o });
  }

  // 2 and 3. Rules and back orders, for lines not already decided.
  for (const { p, o } of out.values()) {
    if (p.route) continue;
    if (!(p.qty > 0)) { p.route = 'skip'; p.reason.push('Nothing needed'); continue; }
    if (!p.vendor_id) { p.route = 'hold'; p.reason.push('No vendor known for this item'); continue; }
    const ir = itemRuleFor(itemRules, p.item);
    if (ir && !(o && o.qty != null && o.qty !== '')) {
      const q = applyQuantityRule(p.qty, ir);
      if (q.qty !== p.qty) { p.qty = q.qty; p.reason.push('Item rule: ' + q.why.join(', ')); }
    }
    const bo = backOrders.find(b => b.vendor_id === p.vendor_id && keyOf(b.item_number) === keyOf(p.item));
    if (bo) {
      p.route = 'hold';
      p.reason.push('On back order' + (bo.expected_until ? ' until ' + bo.expected_until : '') + (bo.use_instead ? '; use ' + bo.use_instead + ' instead' : ''));
      continue;
    }
    if (ir && ir.routing_override) { p.route = ir.routing_override; p.reason.push('Item rule: always ' + ROUTES[ir.routing_override].toLowerCase()); continue; }
    const vr = vendorRules.get(p.vendor_id);
    if (vr && vr.special_mode === 'uncond_transfer') { p.route = 'transfer'; p.reason.push(p.vendor + ' always moves from Austin'); continue; }
    if (vr && vr.special_mode === 'force_order') { p.route = 'order'; p.reason.push(p.vendor + ' is always ordered'); continue; }
  }

  // 4. Minimums, per vendor, over what is still open.
  const byVendor = new Map();
  for (const { p } of out.values()) {
    if (p.route || !p.vendor_id) continue;
    if (!byVendor.has(p.vendor_id)) byVendor.set(p.vendor_id, []);
    byVendor.get(p.vendor_id).push(p);
  }
  for (const [vid, ps] of byVendor) {
    const vr = vendorRules.get(vid);
    const min = vr ? Number(vr.min_order_value) || 0 : 0;
    const total = ps.reduce((s, p) => s + p.qty * (p.cost || 0), 0);
    const forced = ps.find(p => (priorSkips.get(keyOf(p.item)) || 0) >= 2);
    if (total >= min) {
      ps.forEach(p => { p.route = 'order'; p.reason.push(min ? ps[0].vendor + ' reaches its ' + money(min) + ' minimum (' + money(total) + ')' : 'No minimum for ' + p.vendor); });
    } else if (forced) {
      ps.forEach(p => { p.route = 'order'; p.reason.push(forced.item + ' has been skipped twice before for this office, so the ' + p.vendor + ' order goes this time'); });
    } else {
      ps.forEach(p => {
        const spare = austin && p.ae_number ? (austin.get(keyOf(p.ae_number)) || { spare: 0 }).spare : 0;
        if (austin && spare >= p.qty) {
          p.route = 'transfer';
          p.reason.push(p.vendor + ' is below its ' + money(min) + ' minimum (' + money(total) + '), and Austin has ' + spare + ' spare');
        } else {
          p.route = 'skip';
          p.reason.push(p.vendor + ' is below its ' + money(min) + ' minimum (' + money(total) + '), and Austin ' + (austin ? 'has ' + spare + ' spare' : 'stock is not loaded'));
        }
      });
    }
  }

  // His route wins last.
  for (const { p, o } of out.values()) {
    if (o && o.route && o.route !== p.route) { p.route = o.route; p.reason.push('Route set by you'); }
    p.changedByHand = !!(o && (o.item != null || o.qty != null || o.route));
  }

  const result = [...out.values()].map(x => x.p).sort((a, b) => a.line_no - b.line_no);
  return { lines: result, vendors: vendorSummary(result, vendorRules) };
}

/* Each vendor's ordered total against its minimum, worked out from the routes
   as they now stand, so a change by hand shows its effect straight away. */
export function vendorSummary(lines, vendorRules) {
  const m = new Map();
  for (const p of lines) {
    if (!p.vendor_id) continue;
    if (!m.has(p.vendor_id)) {
      const vr = vendorRules.get(p.vendor_id);
      m.set(p.vendor_id, { vendor_id: p.vendor_id, vendor: p.vendor, min: vr ? Number(vr.min_order_value) || 0 : 0,
        mode: vr ? vr.special_mode : null, ordered: 0, orderLines: 0, wanted: 0, transfer: 0, skip: 0, hold: 0 });
    }
    const v = m.get(p.vendor_id);
    // What the vendor's lines come to if all were ordered, so a vendor below its
    // minimum shows how far below (12 October 2026).
    if (p.route !== 'hold' && p.qty > 0) v.wanted += p.qty * (p.cost || 0);
    if (p.route === 'order') { v.ordered += p.qty * (p.cost || 0); v.orderLines++; }
    else if (p.route) v[p.route]++;
  }
  return [...m.values()].map(v => ({ ...v, gap: Math.max(0, v.min - (v.orderLines ? v.ordered : v.wanted)), meets: v.ordered >= v.min }))
    .sort((a, b) => (a.vendor || '').localeCompare(b.vendor || ''));
}
